// Shared focus-management primitive for modal dialogs/drawers, implementing
// the WAI-ARIA APG "Dialog (Modal)" focus contract: move focus in on open,
// trap Tab/Shift+Tab at the container's boundary while open, restore focus
// to the trigger on close.
//
// Built fresh rather than generalized from an existing drawer — at the
// time this hook was written, AssignDrawer and ChecklistTemplateDrawer
// both had Escape-to-close but no focus trap/restore. That retrofit has
// since landed (AssignDrawer.tsx, ChecklistTemplateAssignDrawer.tsx, and
// MaintenanceServicePopover.tsx all call useDialogFocus directly) — this
// comment previously described a followup ticket as still open when it
// was already done; corrected 2026-08-12. DelegationNoteDialog's shape
// still doesn't generalize onto this hook — it focuses one fixed input
// with no Tab cycle, since it's a single-field confirm step, not a
// drawer with a list of tab stops.
//
// No focus-trap-react or similar dependency — this app doesn't have one,
// and the boundary-cycle logic below is a handful of lines.

import { useEffect, useRef } from "react";
import type { RefObject } from "react";

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Disabled buttons/inputs match FOCUSABLE_SELECTOR but aren't real tab
// stops. Filtered here so initial-focus and the boundary cycle agree on
// what "first"/"last" means. Reused by both effects below.
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !(el as HTMLButtonElement | HTMLInputElement).disabled
  );
}

export type DialogFocusOptions = {
  /** Called on Escape pressed anywhere inside the container. Opt-in so
   *  the original callers (which run their own Escape handling) keep
   *  their behaviour unchanged. */
  onEscape?: () => void;
  /** Element to focus on open instead of the first focusable child —
   *  e.g. a drawer's primary textarea. Falls back to the first
   *  focusable child when the ref is empty at open time. */
  initialFocusRef?: RefObject<HTMLElement>;
};

export function useDialogFocus(
  isOpen: boolean,
  containerRef: RefObject<HTMLElement>,
  options: DialogFocusOptions = {}
) {
  const { initialFocusRef } = options;
  // Read through a ref so a fresh onEscape closure on every parent render
  // never re-subscribes the listener nor re-runs the open sequence.
  const onEscapeRef = useRef(options.onEscape);
  onEscapeRef.current = options.onEscape;

  // Save/restore the trigger via the effect's own cleanup rather than a
  // second isOpen-flip check — that way the same restore path covers both
  // isOpen going true→false AND the component unmounting outright while
  // still open (e.g. the row the trigger lived on gets removed by the same
  // action that closed the dialog).
  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    const trigger = document.activeElement;

    const preferred = initialFocusRef?.current;
    if (preferred) {
      preferred.focus();
    } else {
      const focusable = container ? getFocusableElements(container) : [];
      focusable[0]?.focus();
    }

    return () => {
      // Guard against a trigger that was itself unmounted — do nothing
      // rather than throwing or focusing something unrelated.
      if (trigger instanceof HTMLElement && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [isOpen, containerRef, initialFocusRef]);

  // Tab-trap: a boundary cycle, not a full trap library. Listener is
  // attached to the container and only while open, so it can't leak past
  // close/unmount.
  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    if (!container) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        const onEscape = onEscapeRef.current;
        if (onEscape) {
          e.stopPropagation();
          onEscape();
        }
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = getFocusableElements(container!);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener("keydown", onKeyDown);
    return () => container.removeEventListener("keydown", onKeyDown);
  }, [isOpen, containerRef]);
}

// For a content swap inside an already-open dialog (e.g. a drawer moving
// from a "pick a provider" panel to a "confirm" panel without closing) —
// move focus to the new panel's heading without re-running the open
// sequence. A swap is not a re-open: it must not re-save the trigger.
// Consumer is expected to have tabIndex={-1} on the heading so it's
// programmatically focusable.
export function focusHeading(headingRef: RefObject<HTMLElement>) {
  headingRef.current?.focus();
}
